import { useEffect, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { TooltipIcon } from "./Tooltip";
import { PermissionsList } from "models/dbModels/permissions/PermissionsList";
import { PermissionEntries } from "models/dbModels/permissions/PermissionEntries";

export interface childLevel {
	title: string;
	key: keyof PermissionsList;
	tooltip?: string;
    display: (keyof PermissionEntries)[];
    disabled: (keyof PermissionEntries)[];
}

export interface parentLevel {
	title: string;
	children: childLevel[];
}

export const visiblePermissions: {key: keyof PermissionsList, values: (keyof PermissionEntries)[]}[] = [
    {key: "businessDashboard", values: ["canView"]},
    {key: "operationsDashboard", values: ["canView"]},
    {key: "managedStock", values: ["canView", "canCreate", "canUpdate", "canDelete", "canImport"]},
    {key: "suppliers", values: ["canView", "canCreate", "canUpdate", "canDelete"]},
    {key: "departments", values: ["canView", "canCreate", "canUpdate", "canDelete", "canImport"]},
    {key: "stockGroups", values: ["canView", "canCreate", "canUpdate", "canDelete"]},
    {key: "manufacturers", values: ["canView", "canCreate", "canUpdate", "canDelete"]},
    {key: "functions", values: ["canView"]},
    {key: "priceGroups", values: ["canView", "canCreate", "canUpdate", "canDelete", "canImport"]},
    {key: "assignPriceGroups", values: ["canView", "canUpdate"]},
    {key: "dispensePricing", values: ["canView", "canUpdate", "canDelete", "canImport"]},
    {key: "preferredBrands", values: ["canView", "canCreate", "canUpdate", "canDelete", "canImport"]},
    {key: "promotions", values: ["canView", "canCreate", "canUpdate", "canDelete", "canImport"]},
    {key: "stores", values: ["canView", "canUpdate"]},
    {key: "storeGroups", values: ["canView", "canCreate", "canUpdate", "canDelete"]},
    {key: "reports", values: ["canView"]},
    {key: "accounts", values: ["canView", "canCreate", "canUpdate"]},
    {key: "lockDownFields", values: ["canView", "canUpdate"]},
    {key: "supplierDeals", values: ["canView", "canCreate", "canUpdate", "canDelete", "canImport"]},
    {key: "supplierGroupDeals", values: ["canView", "canCreate", "canUpdate", "canDelete", "canImport"]},
]

export const disabledPermissions: {key: keyof PermissionsList, values: (keyof PermissionEntries)[]}[] = [

]

function getDisplay(key: keyof PermissionsList) {
    return visiblePermissions.find((i) => i.key === key)?.values ?? [];
}

function getDisabled(key: keyof PermissionsList) {
    return disabledPermissions.find((i) => i.key === key)?.values ?? [];
}

export const permissionsMenu: parentLevel[] = [
	{
		title: "Dashboards",
		children: [
            { title: "Business", key: "businessDashboard", display: getDisplay("businessDashboard"), disabled: getDisabled("businessDashboard") },
            { title: "Operations", key: "operationsDashboard", display: getDisplay("operationsDashboard"), disabled: getDisabled("operationsDashboard") },
		],
	},

	{
		title: "Stocks",
		children: [
			{
				title: "Managed Stock",
				key: "managedStock",
                display: getDisplay("managedStock"),
                disabled: getDisabled("managedStock")
			},
			{
				title: "Suppliers",
				key: "suppliers",
                display: getDisplay("suppliers"),
                disabled: getDisabled("suppliers")
			},
			{
				title: "Departments",
				key: "departments",
                display: getDisplay("departments"),
                disabled: getDisabled("departments")
			},
			{
				title: "Stock Groups",
				key: "stockGroups",
                display: getDisplay("stockGroups"),
                disabled: getDisabled("stockGroups")
			},
			{
				title: "Manufacturers",
				key: "manufacturers",
                display: getDisplay("manufacturers"),
                disabled: getDisabled("manufacturers")
			},
			{
				title: "Store Data",
				key: "functions",
                display: getDisplay("functions"),
                disabled: getDisabled("functions"),
				tooltip: "Permissions to view store data such as stock information",
			},
		],
	},

	{
		title: "Multi-Level Pricing",
		children: [
			{
				title: "Price Groups",
				key: "priceGroups",
                display: getDisplay("priceGroups"),
                disabled: getDisabled("priceGroups")
			},
			{
				title: "Assign Price Groups",
				key: "assignPriceGroups",
                display: getDisplay("assignPriceGroups"),
                disabled: getDisabled("assignPriceGroups")
			},
		],
	},

	{
		title: "Dispense",
		children: [
			{
				title: "Special Pricing",
				key: "dispensePricing",
                display: getDisplay("dispensePricing"),
                disabled: getDisabled("dispensePricing")
			},
			{
				title: "Preferred Brands",
				key: "preferredBrands",
                display: getDisplay("preferredBrands"),
                disabled: getDisabled("preferredBrands")
			},
		],
	},

	{
		title: "Promotions",
		children: [
			{
				title: "Catalogues",
				key: "promotions",
                display: getDisplay("promotions"),
                disabled: getDisabled("promotions")
			},
		],
	},

	{
		title: "Supplier Deals",
		children: [
            {
                title: "Stock Deals",
                key: "supplierDeals",
                display: getDisplay("supplierDeals"),
                disabled: getDisabled("supplierDeals")
            },
            {
                title: "Assorted Deals",
                key: "supplierGroupDeals",
                display: getDisplay("supplierGroupDeals"),
                disabled: getDisabled("supplierGroupDeals")
            }
        ],
	},

	{
        title: "Reports", children: [
            { title: "Store Reports", key: "reports", display: getDisplay("reports"), disabled: getDisabled("reports") },
		]
	},
    
    {
        title: "Store Management", children: [
            { title: "Stores", key: "stores", display: getDisplay("stores"), disabled: getDisabled("stores") },
            { title: "Store Groups", key: "storeGroups", display: getDisplay("storeGroups"), disabled: getDisabled("storeGroups") },
            { title: "Lockdown", key: "lockDownFields", display: getDisplay("lockDownFields"), disabled: getDisabled("lockDownFields") },
        ]
    },
	{
		title: "Admin",
		children: [
			{
				title: "Accounts",
				key: "accounts",
                display: getDisplay("accounts"),
                disabled: getDisabled("accounts")
			},
		],
	},
];

export function getPermissionOptions() {
    let temp: {key: keyof PermissionsList, display: string}[] = [];

    permissionsMenu.forEach((i) => {
        i.children.forEach((j) => {
            temp.push({key: j.key, display: j.title});
        });
    });

    return temp;
}

interface PermissionSelectorProps {
    permissions: PermissionsList;
    onChange?: (permissions: PermissionsList) => void;
}

export function PermissionSelector(props: PermissionSelectorProps){


    function updatePermissions(temp: PermissionsList) {
        props.onChange && props.onChange(temp);
    }


    function getValue(key: keyof PermissionsList, type: keyof PermissionEntries) {
        if (props.permissions[key]) {
            return props.permissions[key][type] as boolean;
        }
        return false;
    }

    function setValue(key: keyof PermissionsList, type: keyof PermissionEntries, checked: boolean) {
        let typeTemp = { ...props.permissions[key] };
        typeTemp[type] = checked;

        let temp: any = { ...props.permissions };
        temp[key] = typeTemp;

        updatePermissions(temp);
    }

    function setMultiValue(children: childLevel[], type: keyof PermissionEntries, checked: boolean) {
        let temp: any = { ...props.permissions };

        children.forEach((i) => {
            if (
                getDisplay(i.key).includes(type) &&
                !getDisabled(i.key).includes(type)
            ) {
                let typeTemp = { ...props.permissions[i.key] };
                typeTemp[type] = checked;

                temp[i.key] = typeTemp;
            }
        });

        updatePermissions(temp);
    }

    function getMultiValue(children: childLevel[], type: keyof PermissionEntries): boolean {
        let filtered = children.filter(
            (i) =>
                getDisplay(i.key).includes(type) && !getDisabled(i.key).includes(type)
        );
        let checked =
            filtered.length > 0
                ? filtered.every((c) => getValue(c.key, type))
                : false;

        return checked;
    }

    return (
        <>
        
            {permissionsMenu.map((p, i) => (
                <div key={i} className="pb-3">
                    {p.children.length > 0 && (
                        <Row className="border pt-2 bg-body-tertiary ">
                            <Col md={2}>
                                <h4>{p.title}</h4>
                            </Col>

                            <Col md={2}>
                                <Form.Group
                                    hidden={
                                        !p.children.some(
                                            (i) =>
                                                getDisplay(i.key).includes("canView") &&
                                                !getDisabled(i.key).includes("canView")
                                        )
                                    }
                                >
                                    <Form.Check
                                        type="checkbox"
                                        label="View"
                                        checked={getMultiValue(
                                            p.children,
                                            "canView"
                                        )}
                                        onChange={(e) =>
                                            setMultiValue(
                                                p.children,
                                                "canView",
                                                e.currentTarget.checked
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group
                                    hidden={
                                        !p.children.some(
                                            (i) =>
                                                getDisplay(i.key).includes("canCreate") &&
                                                !getDisabled(i.key).includes("canCreate")
                                        )
                                    }
                                >
                                    <Form.Check
                                        type="checkbox"
                                        label="Create"
                                        checked={getMultiValue(
                                            p.children,
                                            "canCreate"
                                        )}
                                        onChange={(e) =>
                                            setMultiValue(
                                                p.children,
                                                "canCreate",
                                                e.currentTarget.checked
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group
                                    hidden={
                                        !p.children.some(
                                            (i) =>
                                                getDisplay(i.key).includes("canUpdate") &&
                                                !getDisabled(i.key).includes("canUpdate")
                                        )
                                    }
                                >
                                    <Form.Check
                                        type="checkbox"
                                        label="Update"
                                        checked={getMultiValue(
                                            p.children,
                                            "canUpdate"
                                        )}
                                        onChange={(e) =>
                                            setMultiValue(
                                                p.children,
                                                "canUpdate",
                                                e.currentTarget.checked
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group
                                    hidden={
                                        !p.children.some(
                                            (i) =>
                                                getDisplay(i.key).includes("canDelete") &&
                                                !getDisabled(i.key).includes("canDelete")
                                        )
                                    }
                                >
                                    <Form.Check
                                        type="checkbox"
                                        label="Delete"
                                        checked={getMultiValue(
                                            p.children,
                                            "canDelete"
                                        )}
                                        onChange={(e) =>
                                            setMultiValue(
                                                p.children,
                                                "canDelete",
                                                e.currentTarget.checked
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group
                                    hidden={
                                        !p.children.some(
                                            (i) =>
                                                getDisplay(i.key).includes("canImport") &&
                                                !getDisabled(i.key).includes("canImport")
                                        )
                                    }
                                >
                                    <Form.Check
                                        type="checkbox"
                                        label="Import"
                                        checked={getMultiValue(
                                            p.children,
                                            "canImport"
                                        )}
                                        onChange={(e) =>
                                            setMultiValue(
                                                p.children,
                                                "canImport",
                                                e.currentTarget.checked
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                    {p.children.length > 0 &&
                        p.children.map((c, ii) => (
                            <div key={ii}>
                                <Row className="my-1">
                                    <Col md={2}>
                                        <p className="text-muted ms-1">
                                            {c.title}
                                            {c.tooltip && (
                                                <TooltipIcon
                                                    text={c.tooltip}
                                                    placement="right"
                                                />
                                            )}
                                        </p>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group
                                            hidden={!getDisplay(c.key).includes("canView")}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                label="View"
                                                disabled={getDisabled(c.key).includes(
                                                    "canView"
                                                )}
                                                checked={getValue(c.key, "canView")}
                                                onChange={(e) =>
                                                    setValue(
                                                        c.key,
                                                        "canView",
                                                        e.currentTarget.checked
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group
                                            hidden={!getDisplay(c.key).includes("canCreate")}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                label="Create"
                                                disabled={getDisabled(c.key).includes(
                                                    "canCreate"
                                                )}
                                                checked={getValue(
                                                    c.key,
                                                    "canCreate"
                                                )}
                                                onChange={(e) =>
                                                    setValue(
                                                        c.key,
                                                        "canCreate",
                                                        e.currentTarget.checked
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group
                                            hidden={!getDisplay(c.key).includes("canUpdate")}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                label="Update"
                                                disabled={getDisabled(c.key).includes(
                                                    "canUpdate"
                                                )}
                                                checked={getValue(
                                                    c.key,
                                                    "canUpdate"
                                                )}
                                                onChange={(e) =>
                                                    setValue(
                                                        c.key,
                                                        "canUpdate",
                                                        e.currentTarget.checked
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group
                                            hidden={!getDisplay(c.key).includes("canDelete")}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                label="Delete"
                                                disabled={getDisabled(c.key).includes(
                                                    "canDelete"
                                                )}
                                                checked={getValue(
                                                    c.key,
                                                    "canDelete"
                                                )}
                                                onChange={(e) =>
                                                    setValue(
                                                        c.key,
                                                        "canDelete",
                                                        e.currentTarget.checked
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group
                                            hidden={!getDisplay(c.key).includes("canImport")}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                label="Import"
                                                disabled={getDisabled(c.key).includes(
                                                    "canImport"
                                                )}
                                                checked={getValue(
                                                    c.key,
                                                    "canImport"
                                                )}
                                                onChange={(e) =>
                                                    setValue(
                                                        c.key,
                                                        "canImport",
                                                        e.currentTarget.checked
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                </div>
            ))}

        </>
    )
}